<template>
  <div class="h-full flex flex-col p-4">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <base-toast v-if="!!baseToast.message" :message="baseToast.message" :type="baseToast.type" @close="baseToast = { type: '', message: '' }"></base-toast>

    <section class="h-full flex flex-col">
      <base-card class="h-full flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b">
          <h2 class="text-lg font-semibold">Create Pay-Out Order</h2>
          <button @click="$router.back()" class="text-gray-600 hover:text-gray-800">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <base-spinner v-if="isLoading" class="flex-auto"></base-spinner>
        <div v-else class="flex-auto overflow-y-auto p-6">
          <div class="max-w-2xl mx-auto">
            <!-- Amount -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
              <input
                v-model.trim="amount.val"
                type="number"
                placeholder="Enter amount"
                @blur="clearValidity('amount')"
                @keypress="allowNumbersOnly"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                :class="{ 'border-red-500': !amount.isValid }"
              />
              <p v-if="!amount.isValid" class="text-xs text-red-600 mt-1">{{ amount.msg }}</p>
            </div>

            <!-- Payout Mode -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Payment Mode *</label>
              <div class="flex gap-3 flex-wrap">
                <button
                  v-for="mode in availablePayoutModes"
                  :key="mode"
                  @click="payoutMode = mode"
                  :class="payoutMode === mode ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'"
                  class="flex-1 min-w-[80px] border-2 font-medium rounded-lg px-4 py-2 transition-colors"
                >
                  {{ mode }}
                </button>
              </div>
            </div>

            <!-- UPI Mode Fields -->
            <template v-if="payoutMode === 'UPI'">
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Customer UPI ID *</label>
                <input
                  v-model.trim="customerUPIID.val"
                  type="text"
                  placeholder="Enter customer UPI ID"
                  @blur="clearValidity('customerUPIID')"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                  :class="{ 'border-red-500': !customerUPIID.isValid }"
                />
                <p v-if="!customerUPIID.isValid" class="text-xs text-red-600 mt-1">{{ customerUPIID.msg }}</p>
              </div>
            </template>

            <!-- Bank Transfer Mode Fields (IMPS / NEFT / RTGS) -->
            <template v-if="bankFieldsRequired">
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                <input
                  v-model.trim="accountNumber.val"
                  type="text"
                  placeholder="Enter account number"
                  @blur="clearValidity('accountNumber')"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                  :class="{ 'border-red-500': !accountNumber.isValid }"
                />
                <p v-if="!accountNumber.isValid" class="text-xs text-red-600 mt-1">{{ accountNumber.msg }}</p>
              </div>

              <!-- Customer Name doubles as Account Holder Name on the
                   bank-transfer ticket. Backend stores on orders.customerName
                   and tolerates malformed input (logs a warning, stores raw)
                   so this field never blocks order creation. -->
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Customer Name (Account Holder) *</label>
                <input
                  v-model.trim="customerName.val"
                  type="text"
                  maxlength="255"
                  placeholder="Enter customer / account holder name"
                  @blur="clearValidity('customerName')"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                  :class="{ 'border-red-500': !customerName.isValid }"
                />
                <p v-if="!customerName.isValid" class="text-xs text-red-600 mt-1">{{ customerName.msg }}</p>
              </div>

              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">IFSC Code *</label>
                <input
                  v-model.trim="ifsc.val"
                  type="text"
                  placeholder="Enter IFSC code"
                  @blur="clearValidity('ifsc')"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                  :class="{ 'border-red-500': !ifsc.isValid }"
                />
                <p v-if="!ifsc.isValid" class="text-xs text-red-600 mt-1">{{ ifsc.msg }}</p>
              </div>

              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
                <input
                  v-model.trim="bankName.val"
                  type="text"
                  placeholder="Enter bank name"
                  @blur="clearValidity('bankName')"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                  :class="{ 'border-red-500': !bankName.isValid }"
                />
                <p v-if="!bankName.isValid" class="text-xs text-red-600 mt-1">{{ bankName.msg }}</p>
              </div>
            </template>

            <!-- Action Buttons -->
            <div class="flex justify-end gap-3 mt-8">
              <button
                @click="$router.back()"
                class="px-6 py-2 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                @click="submitOrder"
                class="px-6 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg font-medium transition-colors"
              >
                Create Order
              </button>
            </div>
          </div>
        </div>
      </base-card>
    </section>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "CreatePayOutClient",
  data() {
    return {
      isLoading: false,
      error: null,
      baseToast: {
        type: "",
        message: "",
      },
      amount: {
        val: null,
        isValid: true,
        msg: "",
      },
      payoutMode: "IMPS",
      customerUPIID: {
        val: "",
        isValid: true,
        msg: "",
      },
      accountNumber: {
        val: "",
        isValid: true,
        msg: "",
      },
      ifsc: {
        val: "",
        isValid: true,
        msg: "",
      },
      bankName: {
        val: "",
        isValid: true,
        msg: "",
      },
      // Beneficiary name — stored on orders.customerName, displayed
      // throughout the payout UI as "Customer Name (Account Holder)".
      customerName: {
        val: "",
        isValid: true,
        msg: "",
      },
    };
  },
  computed: {
    vendor() {
      return this.$store.getters.vendor;
    },
    availablePayoutModes() {
      // Order shown in the UI
      return ["IMPS", "NEFT", "RTGS", "UPI"];
    },
    bankFieldsRequired() {
      return ["IMPS", "NEFT", "RTGS"].includes(this.payoutMode);
    },
  },
  methods: {
    allowNumbersOnly(event) {
      const charCode = event.which || event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    validateForm() {
      let isValid = true;

      if (!this.amount.val || this.amount.val <= 0) {
        this.amount.isValid = false;
        this.amount.msg = "Amount must be greater than 0.";
        isValid = false;
      }

      if (this.payoutMode === "UPI") {
        if (!this.customerUPIID.val) {
          this.customerUPIID.isValid = false;
          this.customerUPIID.msg = "Customer UPI ID is required.";
          isValid = false;
        }
      } else {
        if (!this.accountNumber.val) {
          this.accountNumber.isValid = false;
          this.accountNumber.msg = "Account number is required.";
          isValid = false;
        }
        if (!this.customerName.val) {
          this.customerName.isValid = false;
          this.customerName.msg = "Customer Name (Account Holder) is required.";
          isValid = false;
        }
        if (!this.ifsc.val) {
          this.ifsc.isValid = false;
          this.ifsc.msg = "IFSC code is required.";
          isValid = false;
        }
        if (!this.bankName.val) {
          this.bankName.isValid = false;
          this.bankName.msg = "Bank name is required.";
          isValid = false;
        }
      }

      return isValid;
    },
    async submitOrder() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      const formData = {
        type: "payout",
        amount: parseInt(this.amount.val),
        payoutMode: this.payoutMode,
      };

      if (this.payoutMode === "UPI") {
        formData.customerUPIID = this.customerUPIID.val;
      } else {
        formData.accountNumber = this.accountNumber.val;
        // Beneficiary / Account Holder Name — stored on orders.customerName.
        formData.customerName = this.customerName.val;
        formData.ifsc = this.ifsc.val;
        formData.bankName = this.bankName.val;
      }

      try {
        const response = await http.post(`/orders/${this.vendor}/createPayOutOrder`, formData);

        if (response.status === 201) {
          this.baseToast.type = "success";
          this.baseToast.message = response.data.message;

          setTimeout(() => {
            this.$router.push("/payout");
          }, 1500);
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create order. Please try again.";
      }

      this.isLoading = false;
    },
    handleError() {
      this.error = null;
    },
  },
};
</script>
